import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  cptModifiersService,
  CreateCptModifierInput,
  UpdateCptModifierInput,
} from "../../services/admin/cptmodifiers.service";

export function useCptModifiersAdmin() {
  return useQuery({
    queryKey: ["admin", "cpt-modifiers"],
    queryFn: cptModifiersService.getAll,
  });
}

export function useCptModifier(id?: string) {
  return useQuery({
    queryKey: ["admin", "cpt-modifiers", id],
    queryFn: () => {
      if (!id) {
        throw new Error("ID is required to fetch CPT modifier");
      }
      return cptModifiersService.getById(id);
    },
    enabled: !!id,
  });
}

export function useCreateCptModifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCptModifierInput) =>
      cptModifiersService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-modifiers"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-modifiers"] });
      notifications.show({
        title: "Success",
        message: "CPT Modifier created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create CPT modifier",
        color: "red",
      });
    },
  });
}

export function useUpdateCptModifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCptModifierInput) =>
      cptModifiersService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-modifiers"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-modifiers"] });
      notifications.show({
        title: "Success",
        message: "CPT Modifier updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update CPT modifier",
        color: "red",
      });
    },
  });
}

export function useDeleteCptModifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cptModifiersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-modifiers"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-modifiers"] });
      notifications.show({
        title: "Success",
        message: "CPT Modifier deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete CPT modifier",
        color: "red",
      });
    },
  });
}

export function useToggleCptModifierActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      cptModifiersService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cpt-modifiers"] });
      queryClient.invalidateQueries({ queryKey: ["cpt-modifiers"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
